import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import { task, event, milestone } from "@planzone/dto";
import { useAuthentication } from "@planzone/view-models";
import { useModal } from "@root/client/modal";
import { MemberAvatar } from "@common/memberAvatar";

import { Breadcrumbs } from "../breadcrumbs";
import { CreationDate } from "../date";
import { Props } from "./types";
import styles from "./styles.scss";

function sanitize(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function MentionInComment({ notification, close }: Props) {
  const { pushView } = useModal();
  const { t } = useTranslation();
  const push = useNavigate();
  const { account } = useAuthentication();
  const [isTruncated, setIsTruncated] = React.useState(
    notification.data.commentContent.length > 50,
  );

  return (
    <div className={styles.notification}>
      <div className={styles.header}>
        <Breadcrumbs
          breadcrumbs={[
            {
              name: notification.data.zoneName,
              click: () => push(`/zones/${notification.data.zoneId}`),
            },
            {
              name: notification.data.projectName,
              click: () => {
                push(`/projects/${notification.data.projectId}`);
                close && close();
              },
            },
            ...(notification.data.elementType !== "PROJECT"
              ? [
                  {
                    name: notification.data.elementName,
                    click: () => {
                      notification.data.elementType === "EVENT"
                        ? pushView({
                            key: UseCases.retrieveEvent,
                            data: {
                              event: {
                                ...event,
                                uuid: notification.data.elementId,
                                name: notification.data.elementName,
                              },
                              isMultiProject: true,
                            },
                          })
                        : notification.data.elementType === "MILESTONE"
                          ? pushView({
                              key: UseCases.retrieveMilestone,
                              data: {
                                milestone: {
                                  ...milestone,
                                  uuid: notification.data.elementId,
                                  name: notification.data.elementName,
                                },
                                isMultiProject: true,
                              },
                            })
                          : notification.data.elementType === "TASK"
                            ? pushView({
                                key: UseCases.retrieveTask,
                                data: {
                                  task: {
                                    ...task,
                                    uuid: notification.data.elementId,
                                    name: notification.data.elementName,
                                  },
                                  isMultiProject: true,
                                },
                              })
                            : pushView({
                                key: UseCases.retrieveList,
                                data: {
                                  listId: notification.data.elementId,
                                  isMultiProject: true,
                                },
                              });
                      close && close();
                    },
                  },
                ]
              : []),
          ]}
        />
        <CreationDate date={notification.createdDate} />
      </div>
      <div className={styles.main}>
        <div className={styles.author}>
          <div className={styles.avatar}>
            <MemberAvatar name={notification.data.authorName} avatarUrl="" />
          </div>
          <p className={styles.authorName}>{notification.data.authorName}</p>
        </div>
        <p className={styles.text}>
          {` ${t("notifications.mentionInComment.commented")} : `}
        </p>
        <p
          className={styles.content}
          dangerouslySetInnerHTML={{
            __html: `« ${`${notification.data.commentContent.slice(0, 50)}${
              isTruncated ? "..." : ""
            }`.replace(
              new RegExp(`\@${sanitize(account.resource.name)}`, "g"),
              `<span class="${styles.mention}">@${account.resource.name}</span>`,
            )} »`,
          }}
        />
        {isTruncated && (
          <span
            className={styles.seeMore}
            onClick={(e) => {
              e.stopPropagation();
              setIsTruncated(false);
            }}
          >
            {t("notifications.mentionInComment.seeMore")}
          </span>
        )}
      </div>
    </div>
  );
}

export { MentionInComment };
